/* tslint:disable:max-line-length */
import { InfoCircleFilled } from '@ant-design/icons';
import { Button, Spinner } from '@mm/components';
import { CloseIcon } from '@mm/icons';
import { useEmailAccountCRUDActions } from '@mm/state/actions/EmailAccountCRUDActions';
import {
  EmailAccount,
  NewEmailAccount,
} from '@mm/state/interfaces/emailAccount';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useChannelConnection } from '../../../editor/features/beta/modules/Automations/AutomationEdit/InputTriggers/use-channel-connection';
import { EmailAccountEditor } from '../../../editor/features/beta/modules/Integrations/NativeIntegrations/components/EmailIntegrations/EmailAccountEditor';
import { useFreeTrialUpgradeModal } from '../FreeTrialUpgradeModal/use-free-trial-upgrade-modal';
import { CloseModalProps } from '../modal-helpers';
import styles from './ActivateEmailAccountModal.module.css';

export const ActivateEmailAccountModal = (props: CloseModalProps) => {
  const { closeModal } = props;
  const { connectChannel } = useChannelConnection(() => {});
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const { openFreeTrialUpgradeModalPromotional } = useFreeTrialUpgradeModal();
  const { getMicrosoftOauthUrl } = useEmailAccountCRUDActions();
  const { getAccounts } = useEmailAccountCRUDActions();

  const [emailAccountUnderEdit, setEmailAccountUnderEdit] = useState<
    NewEmailAccount | EmailAccount
  >();
  const {
    nativeIntegrations: hasNativeIntegrationsFeature,
    microsoft_email: hasMicrosoftEmailFeature,
  } = useSelector(combinedFeatureFlagsSelector);

  const [microsoftAccountPopup, setMicrosoftAccountPopup] =
    useState<Window | null>(null);

  useEffect(() => {
    if (!microsoftAccountPopup) {
      return;
    }
    const onMessage = event => {
      if (event.origin !== new URL(API_URL).origin) {
        return;
      }
      const { data } = event;
      if (data?.type === 'microsoftAccountConnected') {
        microsoftAccountPopup.close();
        setMicrosoftAccountPopup(null);
        getAccounts(facebookPageId);
        closeModal();
      }
    };
    window.addEventListener('message', onMessage);
    const checkIfPopupClosedInterval = setInterval(() => {
      if (microsoftAccountPopup.closed) {
        setMicrosoftAccountPopup(null);
        getAccounts(facebookPageId);
      }
    }, 1000);
    return () => {
      window.removeEventListener('message', onMessage);
      clearInterval(checkIfPopupClosedInterval);
    };
  }, [microsoftAccountPopup]);

  const connectMicrosoftAccount = async () => {
    const popup = window.open(
      await getMicrosoftOauthUrl(facebookPageId),
      'microsoftConnect',
      'popup,width=600,height=600',
    );
    setMicrosoftAccountPopup(popup);
  };
  const createNewManualAccount = () => {
    if (!hasNativeIntegrationsFeature) {
      openFreeTrialUpgradeModalPromotional();
      return;
    }
    setEmailAccountUnderEdit({
      name: '',
      emailAddress: '',
      kind: 'custom',
      credential: {
        smtpServerParams: {
          address: '',
          port: 465,
          userName: '',
          password: '',
          enableSsl: true,
        },
        incomingServerType: 'imap',
        incomingServerParams: {
          address: '',
          port: 993,
          userName: '',
          password: '',
          enableSsl: true,
        },
      },
    } as NewEmailAccount);
  };

  return (
    <div className={styles.container}>
      <CloseIcon
        className={styles.closeIcon}
        onClick={closeModal}
        color="#262626"
      />
      <div className={styles.heading}>Connect Your Email Account</div>

      <div className={styles.hint}>
        <InfoCircleFilled />
        Email accounts ending with @gmail.com are limited to 5 emails per day.
      </div>

      <p className={styles.hintFollowUp}>
        We encourage you to link a business email, such as a Google workspace
        email account, which is not limited in this way.
      </p>

      <div className={styles.buttons}>
        <Button
          size="md"
          color="secondary-blue"
          css={{ container: styles.button }}
          onClick={() => connectChannel('email')}
          leftIcon={
            <svg width="21" height="21" viewBox="0 0 186.69 190.5">
              <g transform="translate(1184.583 765.171)">
                <path
                  clipPath="none"
                  mask="none"
                  d="M-1089.333-687.239v36.888h51.262c-2.251 11.863-9.006 21.908-19.137 28.662l30.913 23.986c18.011-16.625 28.402-41.044 28.402-70.052 0-6.754-.606-13.249-1.732-19.483z"
                  fill="#4285f4"
                />
                <path
                  clipPath="none"
                  mask="none"
                  d="M-1142.714-651.791l-6.972 5.337-24.679 19.223h0c15.673 31.086 47.796 52.561 85.03 52.561 25.717 0 47.278-8.486 63.038-23.033l-30.913-23.986c-8.486 5.715-19.31 9.179-32.125 9.179-24.765 0-45.806-16.712-53.34-39.226z"
                  fill="#34a853"
                />
                <path
                  clipPath="none"
                  mask="none"
                  d="M-1174.365-712.61c-6.494 12.815-10.217 27.276-10.217 42.689s3.723 29.874 10.217 42.689c0 .086 31.693-24.592 31.693-24.592-1.905-5.715-3.031-11.776-3.031-18.098s1.126-12.383 3.031-18.098z"
                  fill="#fbbc05"
                />
                <path
                  d="M-1089.333-727.244c14.028 0 26.497 4.849 36.455 14.201l27.276-27.276c-16.539-15.413-38.013-24.852-63.731-24.852-37.234 0-69.359 21.388-85.032 52.561l31.692 24.592c7.533-22.514 28.575-39.226 53.34-39.226z"
                  fill="#ea4335"
                  clipPath="none"
                  mask="none"
                />
              </g>
            </svg>
          }
        >
          Connect with Google
        </Button>

        {hasMicrosoftEmailFeature && (
          <Button
            size="md"
            color="secondary-blue"
            css={{ container: styles.button }}
            onClick={e => {
              e?.stopPropagation();
              connectMicrosoftAccount();
            }}
            leftIcon={
              microsoftAccountPopup ? (
                <Spinner standalone />
              ) : (
                <svg width="21" height="21" viewBox="0 0 21 21">
                  <rect x="1" y="1" width="9" height="9" fill="#f25022" />
                  <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
                  <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
                  <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
                </svg>
              )
            }
            disabled={!!microsoftAccountPopup}
          >
            Connect with Microsoft
          </Button>
        )}
        <Button
          size="md"
          color="secondary-blue"
          onClick={e => {
            e?.stopPropagation();
            createNewManualAccount();
          }}
          leftIcon={<div />}
          css={{ container: styles.button }}
        >
          Connect Manually
        </Button>
      </div>
      <EmailAccountEditor
        emailAccountUnderEdit={emailAccountUnderEdit}
        onClose={closeModal}
      />
    </div>
  );
};
